// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import {
  selectCertificatePrice,
  fetchCertificatesPrices,
} from '@gandi/redux-resource-price';
import type {
  CertificateTypeOption,
  CertificateProtectOption,
  CertificateDigicertWarrantyOption,
  CertificateSectigoWarrantyOption,
  CertificateProviderOption,
  SelectCertificatePriceArgs,
} from '@gandi/redux-resource-price';
import { selectCurrentId } from '@gandi/redux-sharing';

import { useAppSelector } from 'src/hooks/useAppSelector';
import type { Props as FormattedPriceProps } from 'src/shared/Price';
import type { PreFetchReturn, PreFetchArgs, FullState } from 'src/types';

/**
 * Load prices which will be used in the wizard
 * (cf. preFetchCertificatesPrices for prefetching)
 */
export const useCertificatePrices = ({
  process,
}: {
  process: 'create' | 'renew',
}) => {
  const dispatch = useDispatch();
  // TODO: dispatch grid selection if it has not been done yet
  // $FlowFixMe[incompatible-call]
  const { currency, grid } = useAppSelector(selectPricingInfos);
  const sharingId = useAppSelector(selectCurrentId);

  React.useEffect(() => {
    // fetch or refetch prices on mount to make sure data is up-to-date
    dispatch(
      fetchCertificatesPrices({
        process,
        productType: 'certificate',
        grid,
        currency,
        sharingId,
      }),
    );
  }, [currency, dispatch, grid, process, sharingId]);
};

/**
 * prefetch prices (cf. useCertificatePrices)
 */
export const preFetchCertificatesPrices = ({
  dispatch,
  getState,
  process,
}: PreFetchArgs<
  {
    process: 'create' | 'renew',
  },
  {},
  empty,
  FullState,
>): PreFetchReturn<> => {
  // $FlowFixMe[incompatible-call]
  const { currency, grid } = selectPricingInfos(getState());
  const sharingId = selectCurrentId(getState());

  return dispatch(
    fetchCertificatesPrices({
      process,
      productType: 'certificate',
      grid,
      currency,
      sharingId,
    }),
  );
};

type UsePriceProps = {
  type: CertificateTypeOption,
  provider: CertificateProviderOption,
  process: 'create' | 'renew',
};

function useFormattedPrice(props: {
  ...Partial<$Exact<SelectCertificatePriceArgs>>,
  ...UsePriceProps,
  protect: CertificateProtectOption,
}): void | FormattedPriceProps {
  // $FlowFixMe[incompatible-call]
  const { currency, grid } = useAppSelector(selectPricingInfos);
  const showExcludeVatMention = useAppSelector(selectTaxesDisplaySetting);

  const catalogPrices = useAppSelector(state =>
    selectCertificatePrice(state, {
      currency,
      grid,
      productType: 'certificate',
      ...props,
    }),
  );

  if (!catalogPrices) return undefined;
  return {
    currency,
    price: catalogPrices.priceBeforeTaxes,
    priceCrossedOut: catalogPrices.normalPriceBeforeTaxes,
    showExcludeVatMention,
  };
}

type UseTypePrices = UsePriceProps => {
  priceSingle: void | FormattedPriceProps,
  priceMulti: void | FormattedPriceProps,
  priceWildcard: void | FormattedPriceProps,
};

export const useTypePrices: UseTypePrices = props => {
  const priceSingle = useFormattedPrice({ ...props, protect: 'single' });
  const priceMulti = useFormattedPrice({
    ...props,
    protect: 'multi',
    numberAddresses: '3',
  });
  const priceWildcard = useFormattedPrice({ ...props, protect: 'wildcard' });

  return {
    priceSingle,
    priceMulti,
    priceWildcard,
  };
};

type UseMultiAdrressesPrices = ({
  provider: CertificateProviderOption,
  type: CertificateTypeOption,
  protect: CertificateProtectOption,
  warranty:
    | CertificateDigicertWarrantyOption
    | CertificateSectigoWarrantyOption,
  process: 'create' | 'renew',
}) => {
  price3Addresses: void | FormattedPriceProps,
  price5Addresses: void | FormattedPriceProps,
  price10Addresses: void | FormattedPriceProps,
  price20Addresses: void | FormattedPriceProps,
};
export const useMultiAdrressesPrices: UseMultiAdrressesPrices = props => {
  const price3Addresses = useFormattedPrice({
    ...props,
    numberAddresses: '3',
  });
  const price5Addresses = useFormattedPrice({
    ...props,
    numberAddresses: '5',
  });
  const price10Addresses = useFormattedPrice({
    ...props,
    numberAddresses: '10',
  });
  const price20Addresses = useFormattedPrice({
    ...props,
    numberAddresses: '20',
  });

  return {
    price3Addresses,
    price5Addresses,
    price10Addresses,
    price20Addresses,
  };
};
